import { startServoToTargetObject } from "../../ik/ikTarget/pickPlaceServo";
import { isIkServoSessionActive, stopActiveServoSession } from "../../ik/ikTarget/servoSession";
import { useFileStore } from "../../stores/fileStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { applyWorldPositionIk } from "../ikOneShot";
import { resolveEndEffectorBodyUuid } from "../endEffectorResolve";
import { getBehaviorTrainedModel } from "../trainedModels";
import { buildIkChainFromScene } from "../../ik/chainBuilder";
import { resolveTopmostAncestorBodyUuid } from "../resolveTopmostBody";
import { setTrainedModelRuntimeStatus } from "../trainedModelRuntimeStatus";
import { supportsWasmSimd } from "../wasmFeatureSupport";
import { logger } from "../../utils/logger";
import type { BodyUUID } from "../../types/scene";
import type { BTLeaf } from "../types";
import type { IkOrientationMode } from "../types";
import type { LeafContext, TickResult } from "./context";
import {
  resolveRuntimeBodyPos,
  resolveRuntimeBodyTargetPos,
  resolveRuntimeSitePosByName,
} from "./helpers";

type BrowserTrainedModelRuntimeModule = typeof import("../browserTrainedModelRuntime");
type SceneActuatorRef = { joint?: string };
type SceneJointRef = { name?: string };
type SceneWithActuatorsAndJoints = {
  actuators?: Record<string, SceneActuatorRef | undefined>;
  joints?: Record<string, SceneJointRef | undefined>;
};
type TrainedModelDebugGlobal = typeof globalThis & {
  __CRITTER_TRAINED_MODEL_DEBUG?: boolean;
};

const DEFAULT_MOTION_SETTLE_SECONDS = 0.45;
/** Maximum time a move step is allowed to run before it gives up. */
const MOTION_TIMEOUT_MULTIPLIER = 10;
const MOTION_MIN_TIMEOUT_SECONDS = 2.5;
/** Max distance (m) from runtime EE to commanded target to consider the move "arrived". */
const MOTION_CONVERGENCE_TOLERANCE_M = 0.05;
/** Min dwell time at convergence to treat motion as settled (guards against PD overshoot). */
const MOTION_CONVERGENCE_DWELL_MS = 120;
const MOTION_DIAG_INTERVAL_MS = 400;
const TRAINED_MODEL_WASM_SIMD_ERROR =
  "Browser-side trained model inference requires WebAssembly SIMD. This browser does not support the ONNX WASM runtime used by Critter.";

function hasAnyActuatorForChain(
  ctx: LeafContext,
  chain: ReturnType<typeof buildIkChainFromScene>
): boolean {
  if (!chain) return false;
  const scene = ctx.scene as SceneWithActuatorsAndJoints;
  const actuatorEntries = Object.values(scene.actuators || {});
  if (!actuatorEntries.length) return false;
  const jointsByUuid = scene.joints || {};
  const jointIds = chain.links
    .filter(
      (link) =>
        (link.kind === "hinge" || link.kind === "slide") && typeof link.jointUuid === "string"
    )
    .map((link) => link.jointUuid as string);
  if (!jointIds.length) return false;
  return jointIds.some((jointUuid) => {
    const jointName = jointsByUuid?.[jointUuid]?.name || null;
    return actuatorEntries.some(
      (actuator) => actuator?.joint === jointUuid || (jointName && actuator?.joint === jointName)
    );
  });
}

/**
 * Run a motion step as a continuous servo:
 *  - Solves IK on every tick (matches the 30Hz IK controller) so the PD loop keeps tracking.
 *  - After `settleSeconds`, checks runtime EE distance to target. If within tolerance for
 *    `MOTION_CONVERGENCE_DWELL_MS`, returns success.
 *  - If the overall deadline passes without convergence, returns failure with a clear reason.
 */
function servoMotionStep(
  ctx: LeafContext,
  nodeId: string,
  label: string,
  endEffectorBodyUuid: BodyUUID,
  targetXyz: [number, number, number],
  useGripperTargetSite: boolean,
  endEffectorRef: string,
  orientationMode: IkOrientationMode,
  orientationStrength: number,
  orientationTargetBodyUuid: string,
  settleSeconds: number
): TickResult {
  const lm = ctx.getLeafMem(nodeId);
  const now = performance.now();
  const holdSeconds = Math.max(0, settleSeconds);
  const timeoutSeconds = Math.max(
    4.5,
    holdSeconds * MOTION_TIMEOUT_MULTIPLIER,
    MOTION_MIN_TIMEOUT_SECONDS
  );
  if (!lm.motionIssuedAtMs) {
    lm.motionIssuedAtMs = now;
    lm.motionHoldUntilMs = now + holdSeconds * 1000;
    lm.motionTimeoutAtMs = now + timeoutSeconds * 1000;
    lm.motionTargetWorldXyz = [targetXyz[0], targetXyz[1], targetXyz[2]];
    lm.motionConvergedAtMs = undefined;
  }
  const ok = applyWorldPositionIk({
    endEffectorUuid: endEffectorBodyUuid,
    worldXyz: targetXyz,
    useGripperTargetSite,
    orientationMode,
    orientationStrength,
    orientationTargetBodyUuid,
  });
  lm.motionIkFailCount = ok ? 0 : (Number(lm.motionIkFailCount) || 0) + 1;
  const runtimeEe =
    useGripperTargetSite && endEffectorRef === "gripper_target"
      ? resolveRuntimeSitePosByName(ctx.scene, "gripper_target")
      : resolveRuntimeBodyPos(endEffectorBodyUuid);
  const distance = runtimeEe
    ? Math.hypot(runtimeEe.x - targetXyz[0], runtimeEe.y - targetXyz[1], runtimeEe.z - targetXyz[2])
    : Number.POSITIVE_INFINITY;
  const withinTolerance = distance <= MOTION_CONVERGENCE_TOLERANCE_M;
  if (withinTolerance) {
    if (lm.motionConvergedAtMs == null) lm.motionConvergedAtMs = now;
  } else {
    lm.motionConvergedAtMs = undefined;
  }
  const holdElapsed = now >= (lm.motionHoldUntilMs || 0);
  const dwelledLongEnough =
    lm.motionConvergedAtMs != null && now - lm.motionConvergedAtMs >= MOTION_CONVERGENCE_DWELL_MS;
  if (holdElapsed && withinTolerance && dwelledLongEnough) {
    ctx.clearLeafMem(nodeId);
    return "success";
  }
  const timedOut = now >= (lm.motionTimeoutAtMs || 0);
  if (timedOut) {
    const distCm = Number.isFinite(distance) ? (distance * 100).toFixed(1) : "?";
    const ikFailCount = Number(lm.motionIkFailCount) || 0;
    ctx.setFailureReason(
      `${label}: EE did not converge to target within ${timeoutSeconds.toFixed(1)}s (runtime EE was ${distCm}cm from target; IK retries with no solution: ${ikFailCount}).`
    );
    ctx.clearLeafMem(nodeId);
    return "failure";
  }
  if (lm.motionDiagLastAtMs == null || now - lm.motionDiagLastAtMs >= MOTION_DIAG_INTERVAL_MS) {
    lm.motionDiagLastAtMs = now;
    const ee = runtimeEe
      ? {
          x: Number(runtimeEe.x.toFixed(4)),
          y: Number(runtimeEe.y.toFixed(4)),
          z: Number(runtimeEe.z.toFixed(4)),
        }
      : null;
    const tgt = {
      x: Number(targetXyz[0].toFixed(4)),
      y: Number(targetXyz[1].toFixed(4)),
      z: Number(targetXyz[2].toFixed(4)),
    };
    logger.info("[behavior/move-diag]", {
      nodeId,
      label,
      useGripperTargetSite,
      endEffectorRef,
      ikOk: ok,
      ikFailCount: Number(lm.motionIkFailCount) || 0,
      distanceM: Number.isFinite(distance) ? Number(distance.toFixed(4)) : null,
      toleranceM: MOTION_CONVERGENCE_TOLERANCE_M,
      holdElapsed,
      timedOut,
      ee,
      target: tgt,
    });
  }
  ctx.setActiveNode(nodeId);
  return "running";
}

export function tickMoveEeToXyz(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "moveEeToXyz" }>
): TickResult {
  const resolved = resolveEndEffectorBodyUuid(ctx.scene, node.endEffectorRef);
  if (!resolved) {
    ctx.setFailureReason(`Move EE → XYZ: end-effector ref "${node.endEffectorRef}" was not found.`);
    return "failure";
  }
  const chainRoot = resolveTopmostAncestorBodyUuid(ctx.scene, resolved.bodyUuid);
  const chain = buildIkChainFromScene(ctx.scene, resolved.bodyUuid, chainRoot || undefined);
  if (!chain) {
    ctx.setFailureReason(
      `Move EE → XYZ: end-effector ref "${node.endEffectorRef}" is not on an articulated hinge/slide chain.`
    );
    return "failure";
  }
  if (!hasAnyActuatorForChain(ctx, chain)) {
    ctx.setFailureReason(
      `Move EE → XYZ: no actuator controls the hinge/slide joints for end-effector ref "${node.endEffectorRef}".`
    );
    return "failure";
  }
  const lm = ctx.getLeafMem(node.id);
  const settleSeconds = Math.max(0, Number(node.settleSeconds) || DEFAULT_MOTION_SETTLE_SECONDS);
  let target: [number, number, number];
  if (Array.isArray(lm.motionTargetWorldXyz) && lm.motionTargetWorldXyz.length === 3) {
    target = [lm.motionTargetWorldXyz[0], lm.motionTargetWorldXyz[1], lm.motionTargetWorldXyz[2]];
  } else if (node.targetBodyUuid) {
    const anchor = resolveRuntimeBodyTargetPos(node.targetBodyUuid);
    if (!anchor) {
      ctx.setFailureReason(
        `Move EE → XYZ: target body "${node.targetBodyUuid}" could not be resolved in the runtime scene.`
      );
      return "failure";
    }
    target = [anchor.x + node.x, anchor.y + node.y, anchor.z + node.z];
  } else {
    target = [node.x, node.y, node.z];
  }
  const label = (node.label && node.label.trim()) || "Move EE → XYZ";
  return servoMotionStep(
    ctx,
    node.id,
    label,
    resolved.bodyUuid,
    target,
    resolved.useGripperTargetSite,
    node.endEffectorRef,
    node.orientationMode === "alignWithGrasp"
      ? "alignWithGrasp"
      : node.orientationMode === "keepDown"
        ? "keepDown"
        : "free",
    Math.max(0, Math.min(2, Number(node.orientationStrength) || 0.8)),
    node.targetBodyUuid || "",
    settleSeconds
  );
}

export function tickMoveEeByOffset(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "moveEeByOffset" }>
): TickResult {
  const resolved = resolveEndEffectorBodyUuid(ctx.scene, node.endEffectorRef);
  if (!resolved) {
    ctx.setFailureReason(
      `Move EE offset: end-effector ref "${node.endEffectorRef}" was not found.`
    );
    return "failure";
  }
  const chainRoot = resolveTopmostAncestorBodyUuid(ctx.scene, resolved.bodyUuid);
  const chain = buildIkChainFromScene(ctx.scene, resolved.bodyUuid, chainRoot || undefined);
  if (!chain) {
    ctx.setFailureReason(
      `Move EE offset: end-effector ref "${node.endEffectorRef}" is not on an articulated hinge/slide chain.`
    );
    return "failure";
  }
  if (!hasAnyActuatorForChain(ctx, chain)) {
    ctx.setFailureReason(
      `Move EE offset: no actuator controls the hinge/slide joints for end-effector ref "${node.endEffectorRef}".`
    );
    return "failure";
  }
  const lm = ctx.getLeafMem(node.id);
  const settleSeconds = Math.max(0, Number(node.settleSeconds) || DEFAULT_MOTION_SETTLE_SECONDS);
  let target: [number, number, number];
  if (Array.isArray(lm.motionTargetWorldXyz) && lm.motionTargetWorldXyz.length === 3) {
    target = [lm.motionTargetWorldXyz[0], lm.motionTargetWorldXyz[1], lm.motionTargetWorldXyz[2]];
  } else {
    const p =
      resolved.useGripperTargetSite && node.endEffectorRef === "gripper_target"
        ? resolveRuntimeSitePosByName(ctx.scene, "gripper_target")
        : resolveRuntimeBodyPos(resolved.bodyUuid);
    if (!p) {
      ctx.setFailureReason("Move EE offset: could not resolve current end-effector position.");
      return "failure";
    }
    target = [p.x + node.dx, p.y + node.dy, p.z + node.dz];
  }
  const label = (node.label && node.label.trim()) || "Move EE offset";
  return servoMotionStep(
    ctx,
    node.id,
    label,
    resolved.bodyUuid,
    target,
    resolved.useGripperTargetSite,
    node.endEffectorRef,
    node.orientationMode === "alignWithGrasp"
      ? "alignWithGrasp"
      : node.orientationMode === "keepDown"
        ? "keepDown"
        : "free",
    Math.max(0, Math.min(2, Number(node.orientationStrength) || 0.8)),
    "",
    settleSeconds
  );
}

export function tickRunTrainedModel(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "runTrainedModel" }>
): TickResult {
  const debugEnabled = (() => {
    try {
      return (globalThis as TrainedModelDebugGlobal).__CRITTER_TRAINED_MODEL_DEBUG === true;
    } catch {
      return false;
    }
  })();
  const debugLog = (message: string, data?: Record<string, unknown>) => {
    if (!debugEnabled) return;
    if (data) {
      logger.info(`[behavior/model-debug] ${message}`, data);
    } else {
      logger.info(`[behavior/model-debug] ${message}`);
    }
  };
  const model = getBehaviorTrainedModel(node.modelId);
  if (!ctx.physicsOk || !model) {
    ctx.setFailureReason(
      !ctx.physicsOk ? "Trained model: physics is not running." : "Trained model: model not found."
    );
    setTrainedModelRuntimeStatus(node.id, {
      phase: "error",
      lastError: !ctx.physicsOk ? "Physics is not running" : "Model not found",
    });
    return "failure";
  }
  const lm = ctx.getLeafMem(node.id);
  const now = performance.now();
  const durationMs = Math.max(0, Number(node.durationSeconds) || 0) * 1000;
  if (lm.timeElapsedStartMs == null) {
    lm.timeElapsedStartMs = now;
    debugLog("node start", {
      nodeId: node.id,
      modelId: node.modelId,
      modelName: model?.name || null,
      commandVx: node.commandVx,
      commandWz: node.commandWz,
      durationSeconds: node.durationSeconds,
    });
  }
  const elapsedMs = Math.max(0, now - (lm.timeElapsedStartMs || now));
  const isGo2Model = model?.robotKey === "unitree_go2";
  const startupRampMs = 600;
  const startupRamp =
    isGo2Model && startupRampMs > 0 ? Math.max(0, Math.min(1, elapsedMs / startupRampMs)) : 1;
  const effectiveCommandVx = Number(node.commandVx) * startupRamp;
  const effectiveCommandWz = Number(node.commandWz) * startupRamp;
  if (lm.trainedModelFailed) {
    ctx.setFailureReason(`Trained model failed: ${lm.trainedModelError || "unknown error"}`);
    ctx.clearLeafMem(node.id);
    return "failure";
  }
  if (!supportsWasmSimd()) {
    lm.trainedModelFailed = true;
    lm.trainedModelError = TRAINED_MODEL_WASM_SIMD_ERROR;
    setTrainedModelRuntimeStatus(node.id, {
      phase: "error",
      lastError: TRAINED_MODEL_WASM_SIMD_ERROR,
    });
    logger.warn("[behavior] trained model runtime unavailable: WebAssembly SIMD unsupported", {
      modelId: node.modelId,
      name: model?.name || null,
    });
    ctx.setFailureReason(`Trained model failed: ${TRAINED_MODEL_WASM_SIMD_ERROR}`);
    ctx.clearLeafMem(node.id);
    return "failure";
  }
  const minStepGapMs = 20;
  if (now - (lm.timeElapsedStartMs ?? now) >= durationMs) {
    setTrainedModelRuntimeStatus(node.id, { phase: "idle", lastError: null });
    ctx.clearLeafMem(node.id);
    return "success";
  }
  const runtimeMem = lm as typeof lm & {
    trainedModelRuntime?: BrowserTrainedModelRuntimeModule;
    trainedModelRuntimeLoading?: boolean;
  };
  const runtime = runtimeMem.trainedModelRuntime;
  if (!runtime) {
    if (!runtimeMem.trainedModelRuntimeLoading) {
      runtimeMem.trainedModelRuntimeLoading = true;
      setTrainedModelRuntimeStatus(node.id, { phase: "loading", lastError: null });
      import("../browserTrainedModelRuntime")
        .then((module) => {
          runtimeMem.trainedModelRuntime = module;
          runtimeMem.trainedModelRuntimeLoading = false;
        })
        .catch((error) => {
          runtimeMem.trainedModelRuntimeLoading = false;
          runtimeMem.trainedModelFailed = true;
          runtimeMem.trainedModelError = String(error?.message || error);
          setTrainedModelRuntimeStatus(node.id, {
            phase: "error",
            lastError: String(error?.message || error),
          });
          logger.warn("[behavior] failed to load trained model runtime", {
            modelId: node.modelId,
            name: model?.name || null,
            error,
          });
        });
    }
    ctx.setActiveNode(node.id);
    return "running";
  }
  if (!lm.trainedModelReady) {
    if (!lm.trainedModelHoldPending && now - (lm.trainedModelLastStepAt || 0) >= minStepGapMs) {
      lm.trainedModelHoldPending = true;
      runtime
        .runBrowserTrainedModelHoldStep(node.modelId, {
          commandVx: effectiveCommandVx,
          commandWz: effectiveCommandWz,
        })
        .then(() => {
          lm.trainedModelHoldPending = false;
          lm.trainedModelLastStepAt = performance.now();
          if (!lm.trainedModelHoldLoggedOnce) {
            lm.trainedModelHoldLoggedOnce = true;
            debugLog("first hold step completed", {
              nodeId: node.id,
              modelId: node.modelId,
              sinceStartMs: Number((performance.now() - (lm.timeElapsedStartMs || now)).toFixed(1)),
            });
          }
        })
        .catch((error) => {
          lm.trainedModelHoldPending = false;
          logger.warn("[behavior] runTrainedModel hold step failed during startup", {
            modelId: node.modelId,
            name: model?.name || null,
            error,
          });
        });
    }
    if (!lm.trainedModelPending) {
      lm.trainedModelPending = true;
      setTrainedModelRuntimeStatus(node.id, { phase: "loading", lastError: null });
      runtime
        .runBrowserTrainedModelStep(node.modelId, {
          commandVx: effectiveCommandVx,
          commandWz: effectiveCommandWz,
        })
        .then((stats) => {
          lm.trainedModelPending = false;
          lm.trainedModelReady = true;
          lm.trainedModelLastStepAt = performance.now();
          // Signal worker startup-hold handoff only once inference is confirmed live.
          try {
            usePhysicsStore.getState().actions.notifyStartupHoldModelReady({
              modelId: node.modelId,
              nodeId: node.id,
            });
          } catch (error) {
            logger.warn("[behavior] Failed to notify startup hold that model is ready.", error);
          }
          if (!lm.trainedModelReadyLoggedOnce) {
            lm.trainedModelReadyLoggedOnce = true;
            debugLog("model ready (first inference finished)", {
              nodeId: node.id,
              modelId: node.modelId,
              sinceStartMs: Number((performance.now() - (lm.timeElapsedStartMs || now)).toFixed(1)),
              inferenceMs: Number(stats.inferenceMs.toFixed(1)),
            });
          }
          setTrainedModelRuntimeStatus(node.id, {
            phase: "running",
            lastError: null,
            lastInferenceMs: stats.inferenceMs,
          });
        })
        .catch((error) => {
          lm.trainedModelPending = false;
          lm.trainedModelFailed = true;
          lm.trainedModelError = String(error?.message || error);
          setTrainedModelRuntimeStatus(node.id, {
            phase: "error",
            lastError: String(error?.message || error),
          });
          logger.warn("[behavior] runTrainedModel failed during startup", {
            modelId: node.modelId,
            name: model?.name || null,
            error,
          });
        });
    }
    ctx.setActiveNode(node.id);
    return "running";
  }
  if (!lm.trainedModelPending && now - (lm.trainedModelLastStepAt || 0) >= minStepGapMs) {
    lm.trainedModelPending = true;
    runtime
      .runBrowserTrainedModelStep(node.modelId, {
        commandVx: effectiveCommandVx,
        commandWz: effectiveCommandWz,
      })
      .then((stats) => {
        lm.trainedModelPending = false;
        lm.trainedModelLastStepAt = performance.now();
        setTrainedModelRuntimeStatus(node.id, {
          phase: "running",
          lastError: null,
          lastInferenceMs: stats.inferenceMs,
        });
      })
      .catch((error) => {
        lm.trainedModelPending = false;
        lm.trainedModelFailed = true;
        lm.trainedModelError = String(error?.message || error);
        setTrainedModelRuntimeStatus(node.id, {
          phase: "error",
          lastError: String(error?.message || error),
        });
        logger.warn("[behavior] runTrainedModel step failed", {
          modelId: node.modelId,
          name: model?.name || null,
          error,
        });
      });
  }
  ctx.setActiveNode(node.id);
  return "running";
}

export function tickGoToKeyframe(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "goToKeyframe" }>
): TickResult {
  const lm = ctx.getLeafMem(node.id);
  if (lm.keyframeDone) {
    ctx.clearLeafMem(node.id);
    return "success";
  }
  if (lm.keyframeFailed) {
    ctx.clearLeafMem(node.id);
    return "failure";
  }
  if (lm.keyframePending) {
    ctx.setActiveNode(node.id);
    return "running";
  }
  try {
    const actions = useFileStore.getState().actions;
    if (actions && typeof actions.handleKeyframeSelect === "function") {
      const result = actions.handleKeyframeSelect(node.keyframeName);
      if (result && typeof (result as Promise<void>).then === "function") {
        lm.keyframePending = true;
        (result as Promise<void>)
          .then(() => {
            lm.keyframePending = false;
            lm.keyframeDone = true;
          })
          .catch(() => {
            lm.keyframePending = false;
            lm.keyframeFailed = true;
          });
        ctx.setActiveNode(node.id);
        return "running";
      }
    }
    lm.keyframeDone = true;
    return "success";
  } catch {
    ctx.setFailureReason(`Go to keyframe failed for "${node.keyframeName}".`);
    return "failure";
  }
}

export function tickServoPickPlace(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "servoPickPlace" }>
): TickResult {
  if (!ctx.physicsOk) {
    ctx.setFailureReason("Pick & place servo: physics is not running.");
    return "failure";
  }
  const lm = ctx.getLeafMem(node.id);
  const pick = node.pickBodyUuid as BodyUUID;
  const drop = node.dropBodyUuid ? (node.dropBodyUuid as BodyUUID) : null;
  const ee = resolveEndEffectorBodyUuid(ctx.scene, node.endEffectorRef);
  if (!ee || !pick) {
    ctx.setFailureReason("Pick & place servo: missing pick target or end-effector reference.");
    return "failure";
  }
  if (!lm.servoPickStarted) {
    stopActiveServoSession();
    startServoToTargetObject({
      targetBodyUuid: pick,
      endEffectorUuid: ee.bodyUuid,
      useGripperTargetSite: ee.useGripperTargetSite,
      dropoffBodyUuid: drop,
    });
    lm.servoPickStarted = true;
    if (!isIkServoSessionActive()) {
      ctx.setFailureReason("Pick & place servo: failed to start servo session.");
      ctx.clearLeafMem(node.id);
      return "failure";
    }
    ctx.setActiveNode(node.id);
    return "running";
  }
  if (isIkServoSessionActive()) {
    ctx.setActiveNode(node.id);
    return "running";
  }
  ctx.clearLeafMem(node.id);
  return "success";
}
